import { useEffect, useState } from 'react';
import './GoldTrendRadar.css';

const REFRESH_INTERVAL = 300_000; // 5 分钟

const STATUS_COLORS = {
  强多: { color: '#22c55e', bg: 'rgba(34,197,94,0.12)' },
  偏多: { color: '#86efac', bg: 'rgba(134,239,172,0.10)' },
  震荡: { color: '#d4a843', bg: 'rgba(212,168,67,0.10)' },
  偏空: { color: '#fca5a5', bg: 'rgba(252,165,165,0.10)' },
  强空: { color: '#ef4444', bg: 'rgba(239,68,68,0.12)' },
};

const SCORE_LABELS = [
  { label: '强空', score: 10 },
  { label: '偏空', score: 30 },
  { label: '震荡', score: 50 },
  { label: '偏多', score: 70 },
  { label: '强多', score: 90 },
];

function fmtPrice(v) {
  if (v == null || !Number.isFinite(v)) return '—';
  return v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function GoldTrendRadar({ displayPrice }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      try {
        const response = await fetch('/api/gold/vegas-trend');
        if (!response.ok) throw new Error(`API ${response.status}`);
        const result = await response.json();
        if (!cancelled) setData(result);
      } catch {
        // API 不可用，保持 null 显示占位
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    const timer = setInterval(load, REFRESH_INTERVAL);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  const statusStyle = data ? STATUS_COLORS[data.status] : null;
  const fastChannel = data?.channels?.fast || {};
  const slowChannel = data?.channels?.slow || {};
  const slopes = data?.slopes || {};
  const syncedPrice = Number.isFinite(Number(displayPrice)) ? Number(displayPrice) : data?.price;

  // API 不可用 → 优雅占位
  if (!data && !loading) {
    return (
      <div className="gtr-placeholder">
        <div className="gtr-placeholder-icon">📡</div>
        <h3>趋势雷达暂不可用</h3>
        <p>黄金4小时趋势雷达基于维加斯通道 EMA144/169/576/676，需要后端 API 支持。</p>
        <p>后端接口就绪后将自动激活。</p>
      </div>
    );
  }

  // 加载中
  if (loading && !data) {
    return (
      <div className="gtr-placeholder">
        <div className="gtr-placeholder-icon">⏳</div>
        <p>正在加载趋势数据…</p>
      </div>
    );
  }

  // 有数据 → 完整展示
  return (
    <div className="gtr-content">
      {/* 趋势状态大卡 */}
      <div
        className="gtr-status-card"
        style={{
          borderColor: statusStyle?.color || '#d4a843',
          background: statusStyle?.bg || 'rgba(212,168,67,0.06)',
        }}
      >
        <div className="gtr-status-top">
          <span className="gtr-status-label">当前趋势</span>
          <span className="gtr-status-price">${fmtPrice(syncedPrice)}</span>
        </div>
        <div
          className="gtr-status-badge"
          style={{ color: statusStyle?.color || '#d4a843' }}
        >
          {data.status}
        </div>

        {/* 分数进度条 */}
        <div className="gtr-scorebar">
          <div className="gtr-score-labels">
            {SCORE_LABELS.map((s) => (
              <span
                key={s.label}
                className={`gtr-score-tick${data.score >= s.score ? ' active' : ''}`}
              >
                {s.label}
              </span>
            ))}
          </div>
          <div className="gtr-score-track">
            <div
              className="gtr-score-fill"
              style={{
                width: `${Math.max(5, data.score)}%`,
                background: statusStyle?.color || '#d4a843',
              }}
            />
            <div
              className="gtr-score-pointer"
              style={{
                left: `${Math.max(3, data.score)}%`,
                borderColor: statusStyle?.color || '#d4a843',
              }}
            />
          </div>
        </div>
      </div>

      {/* 通道数据 */}
      <div className="gtr-channels">
        <div className="gtr-channel-card">
          <h4>短周期通道</h4>
          <span className="gtr-channel-desc">EMA 144 / 169</span>
          <div className="gtr-channel-grid">
            <div className="gtr-channel-cell">
              <span>上轨</span>
              <strong>{fmtPrice(fastChannel.upper)}</strong>
            </div>
            <div className="gtr-channel-cell">
              <span>下轨</span>
              <strong>{fmtPrice(fastChannel.lower)}</strong>
            </div>
            <div className="gtr-channel-cell">
              <span>EMA144</span>
              <strong>{fmtPrice(fastChannel.ema144)}</strong>
            </div>
            <div className="gtr-channel-cell">
              <span>EMA169</span>
              <strong>{fmtPrice(fastChannel.ema169)}</strong>
            </div>
          </div>
          <div className="gtr-channel-slope">
            方向：<strong>{slopes.fast || '走平'}</strong>
          </div>
        </div>
        <div className="gtr-channel-card">
          <h4>长周期通道</h4>
          <span className="gtr-channel-desc">EMA 576 / 676</span>
          <div className="gtr-channel-grid">
            <div className="gtr-channel-cell">
              <span>上轨</span>
              <strong>{fmtPrice(slowChannel.upper)}</strong>
            </div>
            <div className="gtr-channel-cell">
              <span>下轨</span>
              <strong>{fmtPrice(slowChannel.lower)}</strong>
            </div>
            <div className="gtr-channel-cell">
              <span>EMA576</span>
              <strong>{fmtPrice(slowChannel.ema576)}</strong>
            </div>
            <div className="gtr-channel-cell">
              <span>EMA676</span>
              <strong>{fmtPrice(slowChannel.ema676)}</strong>
            </div>
          </div>
          <div className="gtr-channel-slope">
            方向：<strong>{slopes.slow || '走平'}</strong>
          </div>
        </div>
      </div>

      {/* 趋势解读 */}
      {data.summary && (
        <div className="gtr-summary">
          <h4>📈 趋势解读</h4>
          <p>{data.summary}</p>
        </div>
      )}

      {/* 更新时间 */}
      {data.updatedAt && (
        <p className="gtr-updated">
          数据更新于 {new Date(data.updatedAt).toLocaleString('zh-CN')}
        </p>
      )}
    </div>
  );
}
